// src/beta/pages/Focus.tsx
import { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { AttractLoop } from '@/beta/components/AttractLoop';
import { AxisSwitcher } from '@/beta/components/AxisSwitcher';
import { Eyepatch } from '@/beta/components/Eyepatch';
import { ScoreBars } from '@/beta/components/ScoreBars';
import { useShowcaseStore } from '@/beta/store/showcase';
import { useBetaSettingsStore } from '@/beta/store/settings';
import { useIdleTimer } from '@/beta/hooks/useIdleTimer';
import { getShowcaseBundle } from '@/beta/services/showcase';
import type {
  Axis,
  ShowcaseBundle,
  ProvenanceEntry,
  DocumentEntry,
  ComponentEntry,
  PhaseEntry,
} from '@/beta/types/showcase';
import { PHASE_LABEL } from '@/beta/lib/phase-labels';

const PAGE_PADDING = 28;
const COL_GAP = 16;

/** Focus screen — anchor hero + axis-switcher strip. Spec § 6.2.
 *  Loaded on mount whether the user arrived via Catalog tap or direct URL. */
export function Focus() {
  const { vehicleId = '' } = useParams();
  const navigate = useNavigate();

  const axis = useShowcaseStore((s) => s.axis);
  const setAxis = useShowcaseStore((s) => s.setAxis);
  const setVehicleId = useShowcaseStore((s) => s.setVehicleId);
  const isAttract = useShowcaseStore((s) => s.isAttract);
  const setIsAttract = useShowcaseStore((s) => s.setIsAttract);

  const idleTimeoutMs = useBetaSettingsStore((s) => s.idleTimeoutMs);

  // Sync URL param into the store.
  useEffect(() => {
    setVehicleId(vehicleId || null);
  }, [vehicleId, setVehicleId]);

  // Idle timer → attract flag.
  useIdleTimer({
    timeoutMs: idleTimeoutMs,
    onIdle: () => setIsAttract(true),
    onActive: () => setIsAttract(false),
  });

  const { data: bundle, isLoading, isError } = useQuery({
    queryKey: ['beta', 'showcase', vehicleId],
    queryFn: ({ signal }) => getShowcaseBundle(vehicleId, { signal }),
    staleTime: 5 * 60 * 1000,
    enabled: vehicleId.length > 0,
  });

  if (isLoading) return <FocusSkeleton />;
  if (isError || !bundle) return <FocusError />;

  return (
    <div
      data-vm-beta-focus
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--vm-dark)',
        color: 'var(--vm-warm-white)',
        padding: PAGE_PADDING,
        boxSizing: 'border-box',
      }}
    >
      <FocusHeader bundle={bundle} axis={axis} onAxisChange={setAxis} />

      <main
        style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: '7fr 3fr',
          gap: COL_GAP,
          marginTop: 'var(--vm-s-3)',
          minHeight: 0,
        }}
      >
        <HeroPane bundle={bundle} axis={axis} onTapHero={() => navigate(`/beta/showcase/${vehicleId}/scrub`)} />
        <ThumbRail bundle={bundle} axis={axis} />
      </main>

      <FocusBottomBar bundle={bundle} vehicleId={vehicleId} />

      {isAttract ? (
        <>
          <AttractLoop
            primaryStoryId={bundle?.stories_meta?.[0]?.id ?? null}
            phases={bundle?.phases ?? []}
          />
          <AttractDot />
        </>
      ) : null}
    </div>
  );
}

// ─── Header ─────────────────────────────────────────────────────────────────

function FocusHeader({
  bundle,
  axis,
  onAxisChange,
}: {
  bundle: ShowcaseBundle;
  axis: Axis;
  onAxisChange: (a: Axis) => void;
}) {
  return (
    <header
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 'var(--vm-s-3)',
      }}
    >
      <div
        style={{
          fontFamily: 'var(--vm-font-heading)',
          fontSize: 11,
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          color: 'var(--vm-stone)',
        }}
      >
        <span style={{ color: 'var(--vm-red)' }}>No.&nbsp;{bundle.vehicle.vehicle_number}</span>
        <span style={{ color: 'var(--vm-stone)', margin: '0 12px' }}>·</span>
        <span style={{ color: 'var(--vm-warm-white)' }}>{bundle.vehicle.display_name}</span>
      </div>
      <AxisSwitcher value={axis} onChange={onAxisChange} />
    </header>
  );
}

// ─── Hero pane ──────────────────────────────────────────────────────────────

function HeroPane({
  bundle,
  axis,
  onTapHero,
}: {
  bundle: ShowcaseBundle;
  axis: Axis;
  onTapHero: () => void;
}) {
  if (axis === 'verification') {
    return (
      <section
        style={{
          background: 'var(--vm-dark-card)',
          padding: 'var(--vm-s-4)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--vm-s-3)',
        }}
      >
        <h2
          style={{
            fontFamily: 'var(--vm-font-heading)',
            fontSize: 22,
            letterSpacing: '0.005em',
            margin: 0,
          }}
        >
          Coverage — {bundle.verification.coverage_percent}%
        </h2>
        <ScoreBars parts={bundle.verification.parts} />
        {bundle.verification.next_wins?.length ? (
          <div>
            <div
              style={{
                fontFamily: 'var(--vm-font-heading)',
                fontSize: 10,
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
                color: 'var(--vm-stone)',
                marginBottom: 'var(--vm-s-1)',
              }}
            >
              Next wins
            </div>
            <ul style={{ margin: 0, paddingLeft: 'var(--vm-s-2)' }}>
              {bundle.verification.next_wins.map((w) => (
                <li key={w.id} style={{ color: 'var(--vm-stone-light)', fontSize: 14 }}>
                  {w.label}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>
    );
  }

  if (axis === 'documents') {
    const first = bundle.documents[0];
    if (!first) {
      return <EmptyPane label="No documents on file" />;
    }
    return (
      <section
        style={{
          background: 'var(--vm-dark-card)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--vm-s-2)',
        }}
      >
        <div
          aria-hidden
          style={{
            width: 96,
            height: 120,
            border: '1px solid var(--vm-border)',
            background: 'var(--vm-charcoal)',
          }}
        />
        <div
          style={{
            fontFamily: 'var(--vm-font-heading)',
            fontSize: 11,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: 'var(--vm-stone)',
          }}
        >
          {first.document_type}
        </div>
        <div style={{ fontSize: 11, letterSpacing: '0.3em', fontFamily: 'var(--vm-font-heading)', textTransform: 'uppercase', color: 'var(--vm-stone)' }}>
          {bundle.documents.length} document{bundle.documents.length !== 1 ? 's' : ''}
        </div>
      </section>
    );
  }

  if (axis === 'provenance') {
    const first = bundle.provenance[0];
    if (!first) {
      return <EmptyPane label="No provenance recorded" />;
    }
    return (
      <section
        style={{
          background: 'var(--vm-dark-card)',
          padding: 'var(--vm-s-4)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 'var(--vm-s-2)',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--vm-font-heading)',
            fontSize: 11,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: 'var(--vm-red)',
          }}
        >
          {first.year_from} — {first.year_to ?? 'Present'}
        </div>
        <div
          style={{
            fontFamily: 'var(--vm-font-heading)',
            fontSize: 38,
            textTransform: 'uppercase',
          }}
        >
          {first.name}
        </div>
        <div
          style={{
            fontFamily: 'var(--vm-font-heading)',
            fontSize: 11,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: 'var(--vm-stone-light)',
          }}
        >
          {first.role}
        </div>
        {first.description ? (
          <p style={{ color: 'var(--vm-stone-light)', fontSize: 14, lineHeight: 1.6 }}>
            {first.description}
          </p>
        ) : null}
      </section>
    );
  }

  // phase | component — image-led pane, wrapped in div for data-testid
  const heroSrc =
    axis === 'component'
      ? bundle.components[0]?.hero_image_url
      : bundle.phases[0]?.hero_image_url ?? bundle.vehicle.hero_image_url;
  const caption =
    axis === 'component'
      ? bundle.components[0]?.component
      : bundle.phases[0] ? PHASE_LABEL[bundle.phases[0].phase] ?? bundle.phases[0].phase : undefined;

  return (
    <div
      data-testid="focus-hero"
      style={{ width: '100%', height: '100%' }}
      onClick={axis === 'phase' ? onTapHero : undefined}
    >
      <Eyepatch
        src={heroSrc ?? null}
        alt={bundle.vehicle.display_name}
        caption={caption}
        style={{
          transition: 'opacity 350ms ease',
        }}
      />
    </div>
  );
}

// ─── Thumb rail ────────────────────────────────────────────────────────────

function ThumbRail({ bundle, axis }: { bundle: ShowcaseBundle; axis: Axis }) {
  return (
    <aside
      data-testid="thumb-rail"
      data-axis={axis}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--vm-s-2)',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {axis === 'phase' ? <PhaseThumbs items={bundle.phases.slice(0, 4)} /> : null}
      {axis === 'component' ? <ComponentThumbs items={bundle.components.slice(0, 4)} /> : null}
      {axis === 'provenance' ? <ProvenanceTimeline items={bundle.provenance} /> : null}
      {axis === 'documents' ? <DocumentTiles items={bundle.documents} /> : null}
      {axis === 'verification' ? <MatchingRows bundle={bundle} /> : null}
    </aside>
  );
}

function PhaseThumbs({ items }: { items: PhaseEntry[] }) {
  if (items.length === 0) return <EmptyPane label="No phases yet" inline />;
  return (
    <>
      {items.map((p) => (
        <div
          key={p.phase}
          data-testid="phase-thumb"
          style={{ flex: 1, minHeight: 0, background: 'var(--vm-charcoal)' }}
        >
          <Eyepatch
            src={p.hero_image_url}
            alt={PHASE_LABEL[p.phase] ?? p.phase}
            caption={PHASE_LABEL[p.phase] ?? p.phase}
          />
        </div>
      ))}
    </>
  );
}

function ComponentThumbs({ items }: { items: ComponentEntry[] }) {
  if (items.length === 0) return <EmptyPane label="No components yet" inline />;
  return (
    <>
      {items.map((c) => (
        <div
          key={c.component}
          data-testid="component-thumb"
          style={{ flex: 1, minHeight: 0, background: 'var(--vm-charcoal)' }}
        >
          <Eyepatch src={c.hero_image_url} alt={c.component} caption={c.component} />
        </div>
      ))}
    </>
  );
}

function ProvenanceTimeline({ items }: { items: ProvenanceEntry[] }) {
  if (items.length === 0) return null;
  return (
    <ol
      style={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--vm-s-2)',
      }}
    >
      {items.map((p) => (
        <li
          key={p.id}
          style={{
            borderLeft: '1px solid var(--vm-copper-rule)',
            paddingLeft: 'var(--vm-s-2)',
            paddingTop: 'var(--vm-s-1)',
            paddingBottom: 'var(--vm-s-1)',
          }}
        >
          <div
            style={{
              fontFamily: 'var(--vm-font-heading)',
              fontSize: 18,
              color: 'var(--vm-warm-white)',
            }}
          >
            {p.year_from}
          </div>
          <div
            style={{
              fontFamily: 'var(--vm-font-heading)',
              fontSize: 10,
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              color: 'var(--vm-stone)',
            }}
          >
            {p.role} — {p.name}
          </div>
        </li>
      ))}
    </ol>
  );
}

function DocumentTiles({ items }: { items: DocumentEntry[] }) {
  if (items.length === 0) return null;
  return (
    <>
      {items.slice(0, 4).map((d) => (
        <div
          key={d.id}
          data-testid="document-tile"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--vm-s-2)',
            background: 'var(--vm-dark-card)',
            border: '1px solid var(--vm-border)',
            padding: 'var(--vm-s-2)',
          }}
        >
          <div
            aria-hidden
            style={{
              width: 28,
              height: 36,
              background: 'var(--vm-charcoal)',
              border: '1px solid var(--vm-border)',
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontFamily: 'var(--vm-font-heading)',
                fontSize: 10,
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
                color: 'var(--vm-stone)',
              }}
            >
              {d.document_type}
            </div>
            <div
              style={{
                fontSize: 14,
                color: 'var(--vm-warm-white)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {d.title}
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

function MatchingRows({ bundle }: { bundle: ShowcaseBundle }) {
  if (bundle.matching_components.length === 0) {
    return <EmptyPane label="No matching components recorded" inline />;
  }
  return (
    <>
      {bundle.matching_components.slice(0, 6).map((m) => (
        <div
          key={m.kind}
          data-testid="matching-row"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 'var(--vm-s-1) var(--vm-s-2)',
            background: 'var(--vm-dark-card)',
            border: '1px solid var(--vm-border)',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--vm-font-heading)',
              fontSize: 10,
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              color: 'var(--vm-stone)',
            }}
          >
            {m.kind}
          </span>
          <span
            style={{
              fontFamily: 'var(--vm-font-heading)',
              fontSize: 10,
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              color:
                m.status === 'matching'
                  ? 'var(--vm-red)'
                  : m.status === 'non_matching'
                    ? 'var(--vm-stone)'
                    : 'var(--vm-stone-light)',
            }}
          >
            {m.status.replace('_', ' ')}
          </span>
        </div>
      ))}
    </>
  );
}

// ─── Bottom bar ─────────────────────────────────────────────────────────────

function FocusBottomBar({
  bundle,
  vehicleId,
}: {
  bundle: ShowcaseBundle;
  vehicleId: string;
}) {
  const counts = useMemo(
    () =>
      `${bundle.counts.components} components · ${bundle.counts.photos} photos · ${bundle.counts.phases} phases · ${bundle.counts.documents} documents`,
    [bundle.counts],
  );

  return (
    <footer
      style={{
        marginTop: 'var(--vm-s-3)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderTop: '1px solid var(--vm-border)',
        paddingTop: 'var(--vm-s-2)',
      }}
    >
      <span
        style={{
          fontFamily: 'var(--vm-font-heading)',
          fontSize: 11,
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          color: 'var(--vm-stone-light)',
        }}
      >
        {counts}
      </span>
      <Link
        to={`/beta/showcase/${vehicleId}/dossier`}
        style={{
          fontFamily: 'var(--vm-font-heading)',
          fontSize: 12,
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          color: 'var(--vm-warm-white)',
          background: 'var(--vm-red)',
          padding: '12px 20px',
          textDecoration: 'none',
        }}
      >
        Open Dossier →
      </Link>
    </footer>
  );
}

// ─── Misc ──────────────────────────────────────────────────────────────────

function EmptyPane({ label, inline }: { label: string; inline?: boolean }) {
  return (
    <div
      style={{
        flex: inline ? 1 : undefined,
        minHeight: inline ? 0 : 'auto',
        background: 'var(--vm-dark-card)',
        border: '1px solid var(--vm-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--vm-s-4)',
        fontFamily: 'var(--vm-font-heading)',
        fontSize: 11,
        letterSpacing: '0.3em',
        textTransform: 'uppercase',
        color: 'var(--vm-stone)',
      }}
    >
      {label}
    </div>
  );
}

function FocusSkeleton() {
  return (
    <div
      style={{
        minHeight: '100dvh',
        background: 'var(--vm-dark)',
        color: 'var(--vm-stone)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'var(--vm-font-heading)',
        fontSize: 11,
        letterSpacing: '0.3em',
        textTransform: 'uppercase',
      }}
    >
      Loading vehicle…
    </div>
  );
}

function FocusError() {
  return (
    <div
      style={{
        minHeight: '100dvh',
        background: 'var(--vm-dark)',
        color: 'var(--vm-warm-white)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--vm-s-2)',
      }}
    >
      <p
        style={{
          fontFamily: 'var(--vm-font-heading)',
          fontSize: 11,
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          color: 'var(--vm-red)',
        }}
      >
        Vehicle could not be loaded
      </p>
      <Link
        to="/beta"
        style={{
          color: 'var(--vm-stone-light)',
          fontSize: 14,
          textDecoration: 'underline',
          textUnderlineOffset: '4px',
        }}
      >
        Back to Catalog
      </Link>
    </div>
  );
}

function AttractDot() {
  return (
    <div
      data-testid="attract-dot"
      aria-hidden
      style={{
        position: 'fixed',
        right: 'var(--vm-s-3)',
        bottom: 'var(--vm-s-3)',
        width: 10,
        height: 10,
        borderRadius: '50%',
        background: 'var(--vm-red)',
        animation: 'vm-attract-pulse 1600ms ease-in-out infinite',
      }}
    />
  );
}
